import AsyncSelect from "@/components/AsyncSelect";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Form } from "@/components/ui/form";
import {
  fetchProducts,
  fetchProductGroupList,
  fetchProductList,
} from "@/services/product";
import {
  fetchSurveyTemplate,
  fetchSurveyTemplateList,
} from "@/services/survey-templates";
import { z } from "@/zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useSurvey from "../../hooks/useSurvey";
import createQuestionsFromTemplate from "../../lib/createQuestionsFromTemplate";
import Field from "@/components/Field";
import schema from "./schema";
import { BaseSyntheticEvent, useState } from "react";
import { Hint } from "@/components/ui/hint";
import Fieldset from "@/components/Fieldset";
import { Module, PRODUCT_PLACEHOLDER } from "@/constants";
import SubmitButton from "@/components/SubmitButton";

type TemplateQuestions = z.infer<typeof schema>;

interface QuestionsFromTemplateDialogProps {
  children: React.ReactNode;
  insertAt?: number;
}

export function QuestionsFromTemplateDialog({
  children,
  insertAt,
}: QuestionsFromTemplateDialogProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const survey = useSurvey();

  const form = useForm<TemplateQuestions>({
    resolver: zodResolver(schema),
    defaultValues: {
      enabledProductGroupList: false,
    },
  });

  const enabledProductGroupList = useWatch({
    control: form.control,
    name: "enabledProductGroupList",
  });

  const limit = 100; // TODO: Add pagination support here and in the AsyncSelect component

  const fetchProductsFromProductGroupList = async (
    productGroupListId: string | undefined,
  ) => {
    if (productGroupListId === undefined) {
      return [];
    }
    const productList = await fetchProductList(productGroupListId);
    const productIds = productList.products;
    // TODO: We never implemented this, this is a mock, it will accept anything
    const products = await fetchProducts({
      queryParams: {
        productIds: JSON.stringify(productIds),
      },
    });
    return products.items;
  };

  const handleSubmit = form.handleSubmit(
    async ({ templateId, productGroupListId }: TemplateQuestions) => {
      const template = await fetchSurveyTemplate(templateId);
      const products =
        await fetchProductsFromProductGroupList(productGroupListId);
      const questions = await createQuestionsFromTemplate(template, products);
      survey.addQuestion({
        question: questions,
        insertAt,
      });
      setOpen(false);
    },
  );

  const onSubmit = (event: BaseSyntheticEvent) => {
    event.preventDefault();
    event.stopPropagation();
    handleSubmit(event);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent
        className="w-4/5 max-w-2xl"
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>
            {t("surveyEditor.questionsFromTemplateDialog.title")}
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={onSubmit}>
            <Fieldset>
              <Hint>
                {t("surveyEditor.questionsFromTemplateDialog.hint", {
                  product: PRODUCT_PLACEHOLDER,
                })}
              </Hint>
              <Field
                name="templateId"
                label={t(
                  "surveyEditor.questionsFromTemplateDialog.surveyTemplate",
                )}
                render={({ field }) => (
                  <div className="flex flex-row gap-3">
                    <AsyncSelect
                      queryKey={[Module.SurveyTemplates, { limit }]}
                      queryFn={() => fetchSurveyTemplateList({ limit })}
                      placeholder={t(
                        "surveyEditor.questionsFromTemplateDialog.surveyTemplatePlaceholder",
                      )}
                      loadingLabel={t(
                        "surveyEditor.questionsFromTemplateDialog.loadingSurveyTemplates",
                      )}
                      inDialog
                      {...field}
                    />
                  </div>
                )}
              />
              <Field
                name="enabledProductGroupList"
                className="flex flex-row-reverse items-center justify-end"
                label={t(
                  "surveyEditor.questionsFromTemplateDialog.includeProducts",
                )}
                render={({ field }) => (
                  <Checkbox
                    {...field}
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                )}
              />
              {enabledProductGroupList && (
                <Field
                  name="productGroupListId"
                  label={t(
                    "surveyEditor.questionsFromTemplateDialog.productList",
                  )}
                  render={({ field }) => (
                    <AsyncSelect
                      queryKey={["product-groups"]}
                      queryFn={() => fetchProductGroupList()}
                      placeholder={t(
                        "surveyEditor.questionsFromTemplateDialog.productListPlaceholder",
                      )}
                      loadingLabel={t(
                        "surveyEditor.questionsFromTemplateDialog.loadingProductList",
                      )}
                      {...field}
                    />
                  )}
                />
              )}
            </Fieldset>
            <DialogFooter className="gap-3 pt-2">
              <SubmitButton
                label={t(
                  "surveyEditor.questionsFromTemplateDialog.submitButton",
                )}
                loadingLabel={t(
                  "surveyEditor.questionsFromTemplateDialog.submittingButton",
                )}
                icon={null}
              />
              <DialogClose asChild>
                <Button variant="secondary">
                  {t("surveyEditor.questionsFromTemplateDialog.cancelButton")}
                </Button>
              </DialogClose>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
